import { Array, BigInt, Effect, Option, pipe } from "effect";
import {
  DisplayMarket,
  getProdChainKeyFromProtocol,
  makeUsd,
  ProdProtocolKeyService,
} from "@silo/types";
import { GraphMarket } from "../graph-client/get-graph-markets";
import { getHasUnstableOracle, getWarningConfig } from "@silo/config";
import { getPrimaryDisplayMarketAsset } from "./get-primary-display-market-asset";

export type GetPrimaryDisplayMarket = ReturnType<
  typeof getPrimaryDisplayMarket
>;
export type GetPrimaryDisplayMarketSuccess =
  Effect.Effect.Success<GetPrimaryDisplayMarket>;
export type GetPrimaryDisplayMarketError =
  Effect.Effect.Error<GetPrimaryDisplayMarket>;
export type GetPrimaryDisplayMarketContext =
  Effect.Effect.Context<GetPrimaryDisplayMarket>;

export function getPrimaryDisplayMarket(graphMarket: GraphMarket) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProdProtocolKeyService;
    const chainKey = getProdChainKeyFromProtocol(protocolKey);

    const { marketAddress, isDeprecated, marketAssets, tvlUsd } = graphMarket;

    const [baseAsset, firstBridgeAsset, secondBridgeAsset] = yield* Effect.all(
      [
        getPrimaryDisplayMarketAsset({
          marketAddress,
          assetKey: "base",
          tokenAddress: graphMarket.baseAsset.tokenAddress,
          marketAssets,
        }),
        getPrimaryDisplayMarketAsset({
          marketAddress,
          assetKey: "firstBridge",
          tokenAddress: graphMarket.bridgeAssets[0].tokenAddress,
          marketAssets,
        }),
        pipe(
          graphMarket.bridgeAssets[1],
          Option.fromNullable,
          Option.match({
            onNone: () => Effect.succeed(undefined),
            onSome: ({ tokenAddress }) =>
              getPrimaryDisplayMarketAsset({
                marketAddress,
                assetKey: "secondBridge",
                tokenAddress,
                marketAssets,
              }),
          })
        ),
      ],
      { concurrency: "unbounded" }
    );

    const warningConfig = getWarningConfig({ protocolKey, marketAddress });

    const hasUnstableOracle = getHasUnstableOracle({
      protocolKey,
      marketAddress,
    });

    const assets = pipe(
      [baseAsset, firstBridgeAsset, secondBridgeAsset],
      Array.flatMapNullable((asset) => asset)
    );

    const liquidityUsd = pipe(
      assets,
      Array.map((asset) => asset.liquidityUsd),
      BigInt.sumAll,
      makeUsd
    );

    const displayMarket: DisplayMarket = {
      protocolKey,
      chainKey,
      marketAddress,
      marketSymbol: baseAsset.symbol,
      marketName: baseAsset.name,
      marketTokenAddress: baseAsset.tokenAddress,
      warningConfig,
      hasUnstableOracle,
      isDeprecated,
      liquidityUsd,
      tvlUsd,

      baseAsset,
      bridgeAssets: secondBridgeAsset
        ? [firstBridgeAsset, secondBridgeAsset]
        : [firstBridgeAsset],
    };

    return displayMarket;
  });
}
